/**
 * Карта с машинами.
 *
 * Стиль — VersaTiles Colorful, выбран заказчиком. Ключ не нужен, коммерческое
 * использование разрешено, подпись OpenStreetMap обязательна и приходит вместе
 * со стилем.
 *
 * Значки машин — обычные элементы страницы поверх карты (маркеры MapLibre),
 * а не слой внутри карты: так они переживают смену стиля и обрабатывают
 * нажатия как обычные кнопки.
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import maplibregl, { type Map as MapLibreMap, Marker } from 'maplibre-gl'
import 'maplibre-gl/dist/maplibre-gl.css'

import type { Device, Position, Zone } from './api'
import { circlePolygon } from './geo'
import { type Fix, watchMe } from './geolocate'
import { type Inset, focusOffset, movedEnough, zoomFor } from './follow'
import { brandOf } from './brand'
import { title } from './vehicleId'
import { ART } from './brandArt'
import { CrosshairIcon, LocateIcon } from './icons'
import { dict, type Lang } from './i18n'
import { speedKmh, statusOf, type Status } from './vehicle'

const STYLE_URL = 'https://tiles.versatiles.org/assets/styles/colorful/style.json'

const TRUCK_SVG = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7"
  stroke-linecap="round" stroke-linejoin="round">
  <path d="M2 16V7a1 1 0 0 1 1-1h10a1 1 0 0 1 1 1v9"/>
  <path d="M14 10h3.6a1 1 0 0 1 .82.43l2.4 3.4a1 1 0 0 1 .18.57V16"/>
  <circle cx="7" cy="17.5" r="1.9"/><circle cx="17.5" cy="17.5" r="1.9"/>
  <path d="M9 17.5h6.5M2 16h3"/></svg>`

const TRACK_SOURCE = 'history-track'
const ZONE_SOURCE = 'zones'
const TAIL_SOURCE = 'live-tail'
const CLUSTER_SOURCE = 'fleet-clusters'
const ME_SOURCE = 'me'

/**
 * До какого приближения показывать сгустки вместо машин.
 *
 * На трёхстах машинах карта области — это сплошная каша из значков, по которой
 * ничего не видно. Ближе этого предела значки возвращаются: там уже работают
 * с отдельными машинами, а не смотрят на парк целиком.
 */
const CLUSTER_UNTIL_ZOOM = 9

/** Перелёт к только что выбранной машине, миллисекунды. */
const FOCUS_MS = 900

/**
 * Подтяжка карты за едущей машиной, миллисекунды.
 *
 * Короче промежутка между ответами сервера: карта обязана успеть встать
 * на место и постоять неподвижно, иначе она едет непрерывно и на неё
 * невозможно смотреть.
 */
const FOLLOW_MS = 800

/** Сколько висит сообщение об отказе в доступе к своему положению. */
const NOTE_MS = 5_000

type Props = {
  devices: Device[]
  positions: Map<number, Position>
  selectedId: number | null
  onSelect: (id: number) => void
  /**
   * Счётчик выборов. Растёт на каждом нажатии, даже если выбрали ту же
   * машину: по нему карта возвращается к ней после того, как её увели рукой.
   */
  focusSeq: number
  /**
   * Сколько экрана закрыто своим прямо сейчас — панелью слева или шторкой
   * снизу. Спрашивается в тот миг, когда карта собирается лететь: шторку
   * двигают пальцем, и заранее её высоту не знает никто.
   */
  inset: () => Inset
  /** Язык подписей у кнопок поверх карты. */
  lang: Lang
  center: { lat: number; lon: number }
  /** Ширина панели слева, чтобы машины не оказались под ней. */
  panelInset: { left: number; bottom: number }
  /** Маршрут за период. null — трек не показывается. */
  track?: Position[] | null
  /** Зоны, которые надо нарисовать. */
  zones?: Zone[]
  /** Короткий след за выбранной машиной: откуда она приехала. */
  tail?: Position[] | null
  /** Выбор точки на карте: следующее нажатие вернёт координаты сюда. */
  picking?: boolean
  onPick?: (latitude: number, longitude: number) => void
}

/** Выполнить действие над стилем карты, когда его можно выполнять.
 *
 * `isStyleLoaded()` отвечает не на тот вопрос, который ему тут задают: он
 * ложен не только пока стиль грузится, но и **после каждого `setData`** на
 * любом источнике — до следующего кадра. А живая лента приносит позиции и
 * след одним коммитом React: эффект сгустков успевает сделать `setData`, и
 * идущий следом эффект следа видит ложь. Прежний запасной ход —
 * `once('load')` — на давно загруженной карте не наступает никогда, и след
 * не появлялся вовсе: источник `live-tail` стоял пустым, пока лента росла.
 *
 * `idle` наступает, когда карта дорисовала всё и все источники загружены —
 * в этот миг стиль готов по-настоящему. Слушатель снимается вместе с
 * эффектом: иначе на карте копились бы отложенные действия со старыми
 * данными, и они бы все разом сработали при первом же затишье.
 */
function whenStyled(m: maplibregl.Map, apply: () => void): () => void {
  if (m.isStyleLoaded()) {
    apply()
    return () => {}
  }
  m.once('idle', apply)
  return () => {
    m.off('idle', apply)
  }
}

export function FleetMap({
  devices,
  positions,
  selectedId,
  onSelect,
  focusSeq,
  inset,
  lang,
  center,
  panelInset,
  track = null,
  zones = [],
  tail = null,
  picking = false,
  onPick,
}: Props) {
  const container = useRef<HTMLDivElement>(null)
  const map = useRef<MapLibreMap | null>(null)
  const markers = useRef(new Map<number, Marker>())
  const framed = useRef(false)
  // Обработчик нажатия держим в ссылке: маркеры создаются один раз, а
  // пересоздавать их при каждой отрисовке ради свежего замыкания расточительно.
  const onSelectRef = useRef(onSelect)
  onSelectRef.current = onSelect
  const pickRef = useRef<{ on: boolean; then?: (lat: number, lon: number) => void }>({
    on: false,
  })
  pickRef.current = { on: picking, then: onPick }
  // Положения машин и мерка свободной части экрана нужны перелёту, но зависеть
  // от них он не может: положения приходят каждые пять секунд, и карта
  // улетала бы к машине заново при каждом ответе сервера.
  const positionsRef = useRef(positions)
  positionsRef.current = positions
  const insetRef = useRef(inset)
  insetRef.current = inset

  /**
   * За кем сейчас едет карта.
   *
   * `id` — машина, `auto` — распоряжаемся ли мы ещё приближением, `at` —
   * точка, на которую карту в прошлый раз поставили. Всё это в ссылке, а не
   * в состоянии: перерисовывать экран ради того, что видно только карте,
   * незачем, а перерисовка на каждом ответе сервера ещё и заметна.
   */
  const chase = useRef<{ id: number | null; auto: boolean; at?: Position }>({
    id: null,
    auto: true,
  })

  /**
   * Едет ли карта за машиной — то же самое, но для кнопки-прицела.
   *
   * Второй хозяин у одного знания заведён сознательно: `chase` меняется на
   * каждом ответе сервера, и держать его в состоянии значило бы перерисовывать
   * экран каждые пять секунд. А кнопке нужно ровно одно — да или нет, и
   * меняется это лишь тогда, когда карту берут в руки или возвращают к машине.
   */
  const [chasing, setChasing] = useState(false)
  /**
   * Ушла ли карта так далеко, что значки заменены сгустками.
   *
   * Разбор поймал случай: выбрали машину, отдалили колесом ниже порога
   * сгустков — значок скрыт, кружок сгустка не говорит, что искомая машина
   * внутри, а прицел не появлялся: колесо слежение не снимает, и карта
   * считала, что всё в порядке. Вернуться к машине было нечем, кроме как
   * открыть список и нажать её заново.
   */
  const [afar, setAfar] = useState(false)
  /** Своё положение: показывается, только пока его сами и попросили. */
  const [me, setMe] = useState<Fix | null>(null)
  /**
   * Что сейчас со своей точкой: выключено, ждём первого ответа, показываем.
   *
   * Три состояния, а не «включено да/нет», потому что между нажатием и
   * точкой на карте проходит время. Браузер в отказе не признаётся сразу:
   * когда доступа нет, он просто молчит до конца ожидания — замерено ровно
   * 10 002 мс, — и лишь потом говорит, что оно истекло. Кнопка, которая всё
   * это время горит включённой, врёт, и по ней жмут второй раз.
   */
  const [locating, setLocating] = useState<'off' | 'wait' | 'on'>('off')
  /** Браузер отказал в доступе — сказать об этом вслух, а не промолчать. */
  const [refused, setRefused] = useState(false)
  const unwatch = useRef<(() => void) | null>(null)

  /**
   * Поставить карту так, чтобы точка оказалась в середине свободной части:
   * под панелью слева и шторкой снизу её не видно.
   *
   * Одна на всех: и машина, и своя точка встают на карте одинаково — иначе
   * два места вставали бы по-разному, и разошлись бы они в первый же день.
   */
  const lookAt = useCallback(
    (point: { latitude: number; longitude: number }, zoom: number | null, duration: number) => {
      const m = map.current
      if (!m) return
      const box = m.getContainer()
      m.easeTo({
        center: [point.longitude, point.latitude],
        ...(zoom === null ? {} : { zoom }),
        // Сдвиг, а не padding: padding остаётся в камере навсегда и потом
        // перекашивает всё, что двигает карту после нас.
        offset: focusOffset(insetRef.current(), {
          width: box.clientWidth,
          height: box.clientHeight,
        }),
        duration,
        // `essential` намеренно не ставится: при `prefers-reduced-motion:
        // reduce` MapLibre сам делает такой перелёт мгновенным, и своя
        // проверка была бы вторым хозяином у одного решения.
      })
    },
    [],
  )

  /**
   * Приближение для такой скорости в нынешнем окне.
   *
   * Лестницу по скорости подбирали на телефоне, а метры на точку от размера
   * окна не зависят: на широком экране то же приближение показывает вдвое
   * больше дороги, чем нужно, и выбор машины перестаёт быть нырком к ней —
   * замерено на одном парке, 12,47 → 14,5 в телефоне против 13,9 → 14,5 на
   * компьютере. Свободную часть спрашиваем в тот же миг, что и перелёт:
   * шторку двигают пальцем, и её высоту заранее не знает никто.
   */
  const closeness = useCallback((kmh: number): number | null => {
    const m = map.current
    if (!m) return null
    const box = m.getContainer()
    return zoomFor(kmh, insetRef.current(), {
      width: box.clientWidth,
      height: box.clientHeight,
    })
  }, [])

  /** Повести карту к машине и отметить, что теперь она за ней и едет. */
  const aim = useCallback(
    (position: Position, duration: number) => {
      // Приближением распоряжаемся, только пока его не тронули руками:
      // после щипка карта остаётся такой, какой её оставил человек.
      lookAt(position, chase.current.auto ? closeness(speedKmh(position)) : null, duration)
      chase.current.at = position
      setChasing(true)
    },
    [lookAt, closeness],
  )

  useEffect(() => {
    if (!container.current || map.current) return
    map.current = new maplibregl.Map({
      container: container.current,
      style: STYLE_URL,
      center: [center.lon, center.lat],
      zoom: 11,
      attributionControl: { compact: true },
    })
    // Ручка для отладки: в собранном приложении её нет. Без неё карту нельзя
    // ни о чём спросить из консоли браузера — MapLibre наружу себя не отдаёт.
    if (import.meta.env.DEV) {
      ;(window as unknown as { map?: MapLibreMap }).map = map.current
    }
    return () => {
      map.current?.remove()
      map.current = null
      markers.current.clear()
    }
    // Карта создаётся один раз; последующая смена центра ею не управляет.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Выбрали машину — летим к ней и дальше держим её в кадре. Счётчик выборов
  // в зависимостях затем, чтобы повторное нажатие на ту же машину возвращало
  // к ней карту: сам `selectedId` при этом не меняется.
  useEffect(() => {
    if (selectedId === null) {
      // Выбор сняли — карту не трогаем: человек смотрит туда, куда смотрел.
      chase.current = { id: null, auto: true }
      setChasing(false)
      return
    }
    chase.current = { id: selectedId, auto: true }
    const position = positionsRef.current.get(selectedId)
    // Машины без последней точки на карте нет вовсе. Ждать нечего: как
    // только точка придёт, к ней поведёт слежение ниже.
    if (!position) return

    // Перелёт откладывается на кадр. В тот кадр, где нажали, и без него
    // тесно: React меняет список на карточку, а шторка трогается с места.
    // Карта же в начале перелёта считает, какие плитки ей теперь нужны, —
    // и на телефоне эти работы вместе в кадр не укладываются, отчего
    // подъём шторки спотыкается ровно на первом движении.
    //
    // Замерено на iPhone, по двенадцати выборам подряд: без откладывания
    // кадр роняли все двенадцать, худший кадр в среднем 42 мс; с ним —
    // пять из двенадцати и 32 мс, столько же, сколько до всего слежения.
    // Работы меньше не стало, она просто перестала приходиться на тот
    // единственный кадр, в котором её видно.
    const frame = requestAnimationFrame(() => aim(position, FOCUS_MS))
    return () => cancelAnimationFrame(frame)
  }, [selectedId, focusSeq, aim])

  // Слежение: машина уехала — карта идёт за ней.
  useEffect(() => {
    const followed = chase.current
    if (followed.id === null) return
    const position = positions.get(followed.id)
    // Порог в метрах — против дрожи спутников: у стоящей на погрузке машины
    // точка гуляет, и карта тряслась бы всё это время.
    if (!position || !movedEnough(followed.at, position)) return
    aim(position, FOLLOW_MS)
  }, [positions, aim])

  // Карту увели рукой — слежение снимается, машина остаётся выбранной.
  // Вернуться к ней можно повторным нажатием в списке.
  useEffect(() => {
    const m = map.current
    if (!m) return
    const release = () => {
      chase.current.id = null
      setChasing(false)
    }
    // Щипок, колесо и двойное нажатие слежение не снимают: человек
    // разглядывает ту же машину, а не уходит от неё. Но приближение с этого
    // мига его, а не наше — иначе следующий же ответ сервера отменил бы то,
    // что он только что сделал руками.
    const handled = () => {
      chase.current.auto = false
    }
    const pinch = (event: maplibregl.MapTouchEvent) => {
      // Одним пальцем карту тянут, а не приближают, — это уже разобрал
      // dragstart. Приближение начинается со второго пальца.
      if (event.points.length > 1) handled()
    }
    // Именно эти события, а не `zoomstart`: тот приходит и на свои перелёты
    // тоже, а `originalEvent` MapLibre к нему не прикладывает ни в одном из
    // случаев — отличить по нему руку от карты нельзя. Проверено на живой
    // карте: `wheel`, `dblclick` и `touchmove` бывают только от руки.
    m.on('dragstart', release)
    m.on('wheel', handled)
    m.on('dblclick', handled)
    m.on('touchmove', pinch)
    return () => {
      m.off('dragstart', release)
      m.off('wheel', handled)
      m.off('dblclick', handled)
      m.off('touchmove', pinch)
    }
  }, [])

  // Трек за период. Источник и слой заводятся один раз, дальше меняются
  // только данные — пересоздавать слой на каждое изменение расточительно.
  useEffect(() => {
    const m = map.current
    if (!m) return

    const line = {
      type: 'FeatureCollection' as const,
      features: track && track.length > 1
        ? [{
            type: 'Feature' as const,
            geometry: {
              type: 'LineString' as const,
              coordinates: track.map((p) => [p.longitude, p.latitude]),
            },
            properties: {},
          }]
        : [],
    }

    const apply = () => {
      const existing = m.getSource(TRACK_SOURCE) as maplibregl.GeoJSONSource | undefined
      if (existing) {
        existing.setData(line)
        return
      }
      m.addSource(TRACK_SOURCE, { type: 'geojson', data: line })
      m.addLayer({
        id: TRACK_SOURCE,
        type: 'line',
        source: TRACK_SOURCE,
        layout: { 'line-cap': 'round', 'line-join': 'round' },
        paint: {
          // Тонкая полупрозрачная линия: маршрут за сутки многократно проходит
          // по одним и тем же улицам, и толстая непрозрачная превращает его
          // в сплошное пятно. Прозрачность делает наложения плотностью, по
          // которой видно, где машина бывает чаще.
          'line-color': '#b3401a',
          'line-width': ['interpolate', ['linear'], ['zoom'], 10, 1.5, 14, 2.5, 17, 4],
          'line-opacity': 0.45,
        },
      })
    }

    return whenStyled(m, apply)
  }, [track])

  // Сгустки машин на дальнем плане. Считает их сам MapLibre: своя реализация
  // кластеризации — это заново написанный и заново отлаженный чужой алгоритм.
  useEffect(() => {
    const m = map.current
    if (!m) return

    const points = {
      type: 'FeatureCollection' as const,
      features: devices
        .filter((d) => positions.has(d.id))
        .map((d) => {
          const p = positions.get(d.id)!
          return {
            type: 'Feature' as const,
            geometry: { type: 'Point' as const, coordinates: [p.longitude, p.latitude] },
            properties: { name: d.name },
          }
        }),
    }

    const apply = () => {
      const existing = m.getSource(CLUSTER_SOURCE) as maplibregl.GeoJSONSource | undefined
      if (existing) {
        existing.setData(points)
        return
      }
      m.addSource(CLUSTER_SOURCE, {
        type: 'geojson',
        data: points,
        cluster: true,
        clusterRadius: 55,
        clusterMaxZoom: CLUSTER_UNTIL_ZOOM,
      })
      m.addLayer({
        id: CLUSTER_SOURCE + '-circle',
        type: 'circle',
        source: CLUSTER_SOURCE,
        maxzoom: CLUSTER_UNTIL_ZOOM,
        paint: {
          'circle-color': '#1c1c1e',
          'circle-opacity': 0.86,
          // Крупный сгусток — крупнее кружок: число машин видно и без чтения.
          'circle-radius': ['step', ['get', 'point_count'], 16, 10, 21, 50, 27],
          'circle-stroke-width': 2,
          'circle-stroke-color': 'rgba(255,255,255,0.85)',
        },
        filter: ['has', 'point_count'],
      })
      m.addLayer({
        id: CLUSTER_SOURCE + '-count',
        type: 'symbol',
        source: CLUSTER_SOURCE,
        maxzoom: CLUSTER_UNTIL_ZOOM,
        filter: ['has', 'point_count'],
        layout: {
          'text-field': ['get', 'point_count_abbreviated'],
          'text-font': ['noto_sans_bold'],
          'text-size': 13,
        },
        paint: { 'text-color': '#ffffff' },
      })
      m.addLayer({
        id: CLUSTER_SOURCE + '-lone',
        type: 'circle',
        source: CLUSTER_SOURCE,
        maxzoom: CLUSTER_UNTIL_ZOOM,
        filter: ['!', ['has', 'point_count']],
        paint: {
          'circle-color': '#1c1c1e',
          'circle-radius': 5,
          'circle-stroke-width': 2,
          'circle-stroke-color': 'rgba(255,255,255,0.85)',
        },
      })
    }

    return whenStyled(m, apply)
  }, [devices, positions])

  // Значки машин прячутся, пока карта показывает сгустки: одно и то же на
  // экране дважды сбивает с толку.
  useEffect(() => {
    const m = map.current
    if (!m) return
    const update = () => {
      const far = m.getZoom() < CLUSTER_UNTIL_ZOOM
      for (const marker of markers.current.values()) {
        marker.getElement().classList.toggle('far', far)
      }
      // Состояние меняется только на пересечении порога, а не на каждом
      // кадре зума: перерисовывать экран во время щипка незачем.
      setAfar((было) => (было === far ? было : far))
    }
    update()
    m.on('zoom', update)
    return () => {
      m.off('zoom', update)
    }
  }, [])

  // Своя точка на карте. Круг вокруг неё — не украшение: в городе браузер
  // ошибается на десятки метров, и точка без круга уверенно врёт, будто
  // человек стоит ровно здесь. Круг рисуется многоугольником по той же
  // причине, что и зоны: у круглого слоя MapLibre радиус задан в пикселях,
  // а точность живёт в метрах.
  useEffect(() => {
    const m = map.current
    if (!m) return

    const shapes = {
      type: 'FeatureCollection' as const,
      features: me
        ? [
            {
              type: 'Feature' as const,
              geometry: {
                type: 'Polygon' as const,
                coordinates: [circlePolygon(me.latitude, me.longitude, me.accuracy)],
              },
              properties: {},
            },
            {
              type: 'Feature' as const,
              geometry: {
                type: 'Point' as const,
                coordinates: [me.longitude, me.latitude],
              },
              properties: {},
            },
          ]
        : [],
    }

    const apply = () => {
      const existing = m.getSource(ME_SOURCE) as maplibregl.GeoJSONSource | undefined
      if (existing) {
        existing.setData(shapes)
        return
      }
      m.addSource(ME_SOURCE, { type: 'geojson', data: shapes })
      m.addLayer({
        id: ME_SOURCE + '-halo',
        type: 'fill',
        source: ME_SOURCE,
        filter: ['==', ['geometry-type'], 'Polygon'],
        paint: { 'fill-color': '#0a84ff', 'fill-opacity': 0.12 },
      })
      m.addLayer({
        id: ME_SOURCE + '-dot',
        type: 'circle',
        source: ME_SOURCE,
        filter: ['==', ['geometry-type'], 'Point'],
        paint: {
          'circle-color': '#0a84ff',
          'circle-radius': 7,
          'circle-stroke-width': 3,
          'circle-stroke-color': '#ffffff',
        },
      })
    }

    return whenStyled(m, apply)
  }, [me])

  // Слежение за собой прекращается вместе с экраном: пока оно живо, телефон
  // держит приёмник включённым, и забытое оно съедает батарею молча.
  useEffect(() => () => unwatch.current?.(), [])

  /** Показать себя на карте или перестать. */
  const toggleMe = useCallback(() => {
    if (unwatch.current) {
      unwatch.current()
      unwatch.current = null
      setLocating('off')
      setMe(null)
      return
    }
    setRefused(false)
    setLocating('wait')
    let first = true
    unwatch.current = watchMe(
      (fix) => {
        setMe(fix)
        setLocating('on')
        // Карта едет к себе только на первом ответе. Дальше точка живёт
        // сама: уводить за ней карту — значит отнимать её у человека,
        // который к этому времени уже смотрит куда хочет.
        if (!first) return
        first = false
        // Своя точка — это тоже уход от машины, как и тяга рукой. Прицел
        // рядом загорится сам и вернёт обратно одним нажатием.
        chase.current.id = null
        setChasing(false)
        // Приближение то же, что у стоящей машины: своя точка нужна, чтобы
        // понять, у какого ты подъезда, а не в каком районе города. Берётся
        // из той же таблицы, а не числом рядом: два числа про одно и то же
        // разъезжаются при первой же правке.
        lookAt(fix, closeness(0), FOCUS_MS)
      },
      () => {
        // Отказ, выключенная геолокация, истёкшее ожидание — всё одно: точки
        // не будет. Молчать нельзя, иначе кнопка выглядит сломанной.
        unwatch.current?.()
        unwatch.current = null
        setLocating('off')
        setMe(null)
        setRefused(true)
      },
    )
  }, [lookAt, closeness])

  /** Вернуться к выбранной машине и снова держать её в кадре. */
  const backToVehicle = useCallback(() => {
    if (selectedId === null) return
    chase.current = { id: selectedId, auto: true }
    const position = positionsRef.current.get(selectedId)
    if (position) aim(position, FOCUS_MS)
  }, [selectedId, aim])

  // Живой след за выбранной машиной. Рисуется тоньше и бледнее истории:
  // это подсказка «откуда приехала», а не маршрут, который изучают.
  useEffect(() => {
    const m = map.current
    if (!m) return

    const line = {
      type: 'FeatureCollection' as const,
      features:
        tail && tail.length > 1
          ? [
              {
                type: 'Feature' as const,
                geometry: {
                  type: 'LineString' as const,
                  coordinates: tail.map((p) => [p.longitude, p.latitude]),
                },
                properties: {},
              },
            ]
          : [],
    }

    const apply = () => {
      const existing = m.getSource(TAIL_SOURCE) as maplibregl.GeoJSONSource | undefined
      if (existing) {
        existing.setData(line)
        return
      }
      m.addSource(TAIL_SOURCE, { type: 'geojson', data: line })
      m.addLayer({
        id: TAIL_SOURCE,
        type: 'line',
        source: TAIL_SOURCE,
        layout: { 'line-cap': 'round', 'line-join': 'round' },
        paint: { 'line-color': '#1c1c1e', 'line-width': 2, 'line-opacity': 0.28 },
      })
    }

    return whenStyled(m, apply)
  }, [tail])

  // Зоны. Круг рисуется многоугольником: у круглого слоя MapLibre радиус
  // задаётся в пикселях, а зона живёт в метрах и обязана остаться собой на
  // любом увеличении.
  useEffect(() => {
    const m = map.current
    if (!m) return

    const shapes = {
      type: 'FeatureCollection' as const,
      features: zones
        .filter((zone) => zone.circle)
        .map((zone) => ({
          type: 'Feature' as const,
          geometry: {
            type: 'Polygon' as const,
            coordinates: [
              circlePolygon(zone.circle!.latitude, zone.circle!.longitude, zone.circle!.radius),
            ],
          },
          properties: { name: zone.name },
        })),
    }

    const apply = () => {
      const existing = m.getSource(ZONE_SOURCE) as maplibregl.GeoJSONSource | undefined
      if (existing) {
        existing.setData(shapes)
        return
      }
      m.addSource(ZONE_SOURCE, { type: 'geojson', data: shapes })
      // Заливка бледная, а граница заметная: зона — это фон работы, а не
      // её содержание, и закрашивать под собой карту она не должна.
      m.addLayer({
        id: ZONE_SOURCE + '-fill',
        type: 'fill',
        source: ZONE_SOURCE,
        paint: { 'fill-color': '#2f6fb3', 'fill-opacity': 0.09 },
      })
      m.addLayer({
        id: ZONE_SOURCE + '-line',
        type: 'line',
        source: ZONE_SOURCE,
        paint: { 'line-color': '#2f6fb3', 'line-width': 1.6, 'line-opacity': 0.7 },
      })
    }

    return whenStyled(m, apply)
  }, [zones])

  // Выбор точки для новой зоны.
  useEffect(() => {
    const m = map.current
    if (!m) return

    const handler = (event: maplibregl.MapMouseEvent) => {
      const { on, then } = pickRef.current
      if (!on || !then) return
      then(event.lngLat.lat, event.lngLat.lng)
    }
    m.on('click', handler)
    return () => {
      m.off('click', handler)
    }
  }, [])

  // Курсор-перекрестие говорит, что карта ждёт нажатия, — иначе режим выбора
  // ничем себя не выдаёт.
  useEffect(() => {
    const canvas = map.current?.getCanvas()
    if (canvas) canvas.style.cursor = picking ? 'crosshair' : ''
  }, [picking])

  useEffect(() => {
    const m = map.current
    if (!m) return

    const alive = new Set<number>()

    for (const device of devices) {
      const position = positions.get(device.id)
      if (!position) continue
      alive.add(device.id)

      let marker = markers.current.get(device.id)
      if (!marker) {
        // Внешний элемент отдан MapLibre: он двигает его через transform.
        // Всё оформление — во вложенном .pin-body, иначе значок будет
        // догонять карту вместо того, чтобы стоять на своей точке.
        const element = document.createElement('button')
        element.type = 'button'
        element.className = 'pin'
        const body = document.createElement('span')
        body.className = 'pin-body'
        // Значок марки, если её видно по имени. Текстом, а не разметкой из
        // строки: имя машины задаёт человек, и вставлять его в innerHTML
        // нельзя — это чужой ввод в нашей странице.
        const brand = brandOf(device.name)
        if (brand?.art) {
          // Знак марки — наша константа из brandArt, не имя машины: имя
          // задаёт человек, и в innerHTML оно не попадает никогда.
          const art = document.createElement('span')
          art.className = 'art'
          art.innerHTML = ART[brand.art]
          body.append(art)
        } else if (brand) {
          const word = document.createElement('span')
          word.className = 'brand'
          word.style.fontSize = `${brand.size}px`
          word.textContent = brand.short
          body.append(word)
        } else {
          body.innerHTML = TRUCK_SVG
        }
        // Стрелка направления — отдельным слоем поверх значка: поворачивать
        // сам грузовик нельзя, вверх ногами он читается плохо.
        const arrow = document.createElement('span')
        arrow.className = 'pin-course'
        body.append(arrow)
        element.append(body)
        element.addEventListener('click', (event) => {
          event.stopPropagation()
          // Пока ждут точку для зоны, значок машины — не кнопка, а часть
          // карты: нажатие по нему обязано отдать место, а не выбрать
          // машину. Иначе выходило вот что: панель с недописанной формой
          // зоны уезжала, на её месте оказывалась карточка машины, карта
          // уползала следом за едущей машиной, а надпись «Укажите место»
          // продолжала висеть — и заполненная форма пропадала.
          //
          // Обычно сюда не доходит: в режиме выбора значки не ловят нажатий
          // (см. `.map[data-picking] .pin` в стилях), и место берётся точно
          // под курсором. Это второй заслон, на случай если стилей нет.
          const { on, then } = pickRef.current
          if (on) {
            const at = markers.current.get(device.id)?.getLngLat()
            if (at && then) then(at.lat, at.lng)
            return
          }
          onSelectRef.current(device.id)
        })
        marker = new maplibregl.Marker({ element }).setLngLat([
          position.longitude,
          position.latitude,
        ])
        marker.addTo(m)
        markers.current.set(device.id, marker)
      } else {
        marker.setLngLat([position.longitude, position.latitude])
      }

      const status: Status = statusOf(device, position)
      const element = marker.getElement()
      element.dataset.st = status
      // Направление показываем только на ходу: у стоящей машины курс — это
      // последнее, куда она ехала, и стрелка врала бы.
      const arrow = element.querySelector('.pin-course') as HTMLElement | null
      if (arrow) {
        const moving = status === 'move'
        arrow.style.display = moving ? '' : 'none'
        if (moving) arrow.style.transform = `rotate(${position.course}deg)`
      }
      element.classList.toggle('sel', device.id === selectedId)
        // Подпись для экранного диктора — тот же госномер, что видит глаз:
        // «SIM-01» человек не встретит больше нигде.
      element.setAttribute('aria-label', title(device))
    }

    // машины, которых больше нет в выдаче, убираем с карты
    for (const [id, marker] of markers.current) {
      if (!alive.has(id)) {
        marker.remove()
        markers.current.delete(id)
      }
    }

    // Один раз подгоняем масштаб так, чтобы машины попали в свободную часть
    // карты: слева её закрывает панель, снизу на телефоне — шторка.
    if (!framed.current && alive.size > 0) {
      framed.current = true
      const bounds = new maplibregl.LngLatBounds()
      for (const id of alive) {
        const p = positions.get(id)!
        bounds.extend([p.longitude, p.latitude])
      }
      m.fitBounds(bounds, {
        padding: { top: 80, left: panelInset.left, right: 40, bottom: panelInset.bottom },
        maxZoom: 14,
        duration: 0,
      })
    }
  }, [devices, positions, selectedId, panelInset])

  // Сообщение об отказе живёт несколько секунд: это извещение, а не разговор,
  // и закрывать его вручную человеку незачем.
  useEffect(() => {
    if (!refused) return
    const timer = setTimeout(() => setRefused(false), NOTE_MS)
    return () => clearTimeout(timer)
  }, [refused])

  const t = dict(lang)

  return (
    <>
      <div className="map" ref={container} data-picking={picking ? 'on' : undefined} />

      {/* Кнопки прячутся, пока на карте выбирают точку для зоны: там нужен
          чистый холст, по которому тыкают, а не углы с кнопками. */}
      {!picking && (
        <div className="map-btns">
          {/* Прицел показывается, только когда возвращаться есть куда и есть
              откуда: машина выбрана, а карта за ней уже не едет. */}
          {selectedId !== null && (!chasing || afar) && (
            <button
              type="button"
              className="map-btn"
              onClick={backToVehicle}
              aria-label={t.backToVehicle}
              title={t.backToVehicle}
            >
              <CrosshairIcon />
            </button>
          )}
          <button
            type="button"
            className={'map-btn' + (locating === 'off' ? '' : ' ' + locating)}
            onClick={toggleMe}
            aria-label={t.whereAmI}
            aria-pressed={locating === 'on'}
            aria-busy={locating === 'wait'}
            title={t.whereAmI}
          >
            <LocateIcon />
          </button>
        </div>
      )}

      {refused && (
        <p className="map-note" role="status">
          {t.etaNoLocation}
        </p>
      )}
    </>
  )
}
